import type { Stripe } from '@stripe/stripe-js';
import {
  type PaymentMethodDto,
  type PaymentMethodDtoWithIsPreferred,
  type PaymentMethodWithDocumentsDto,
  keyValueFieldsToObject,
  retryUntilValue,
  useBrokerageFirmControllerGenerateDocumentsForSignature,
  useBrokerageFirmControllerGenerateMandateSignatureRequest,
  useBrokerageFirmControllerDeletePaymentMethod,
  useBrokerageFirmControllerUpdatePreferredPaymentMethod,
  viewSpecificControllerListBrokerageFirmPaymentMethodsQueryOptions,
} from 'korint-sdk';
import { CreditCardIcon, EyeIcon, InfoIcon, LandmarkIcon } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { DocumentDownloadButton } from '@folio/components/module-elements/Documents/DocumentDownloadButton';
import { formatDate } from '@folio/lib';
import { Description, DialogTitle } from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { VisuallyHidden } from 'react-aria';
import { Button } from '../../ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogTrigger } from '../../ui/dialog';
import { Skeleton } from '../../ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { useToast } from '../../ui/use-toast';
import { RemovePaymentMethodDialog } from './RemovePaymentMethodDialog';
import { DocumentViewer } from '../document-viewer/DocumentViewer';
import { useDocumentViewer } from '../document-viewer/useDocumentViewer';
import type { DocumentViewerDocument } from '../document-viewer/types';
import { IntermediaryPaymentMethodSetup } from './IntermediaryPaymentMethodsSetup';
import { PaymentMethodStatusBadge } from './PaymentMethodStatusBadge';
import { isLastChargeableMethod } from './isLastChargeableMethod';
import { PAYMENT_METHOD_WEBHOOK_RETRY } from './paymentMethodWebhookRetry';
import { Group, Stack } from '@folio/components/ui/flex';
import { Badge } from '@folio/components/ui/badge';

export type IntermediaryPaymentMethodsCardProps = {
  brokerageFirmId: string;
  stripePromise: Promise<Stripe | null>;
  readonly?: boolean;
  showMandateSignatureButton?: boolean;
  canBeDefault?(paymentMethod: PaymentMethodDtoWithIsPreferred): boolean;
};

export function IntermediaryPaymentMethodsCard({
  brokerageFirmId,
  stripePromise,
  readonly = false,
  showMandateSignatureButton = true,
  canBeDefault,
}: IntermediaryPaymentMethodsCardProps) {
  const {
    data: paymentMethods,
    status,
    error,
    refetch,
    isRefetching,
  } = useQuery(viewSpecificControllerListBrokerageFirmPaymentMethodsQueryOptions(brokerageFirmId));

  const sepaPaymentMethods = useMemo(
    () => paymentMethods?.data?.filter(pm => pm.paymentMethodType === 'sepa_debit') ?? [],
    [paymentMethods],
  );
  const cardPaymentMethods = useMemo(
    () => paymentMethods?.data?.filter(pm => pm.paymentMethodType === 'card') ?? [],
    [paymentMethods],
  );

  // Deletion is card-scoped so a removal in flight disables every row's Remove
  // action until the refreshed list recalculates `canRemove` — otherwise a second
  // row could delete the now-last selectable method during the refetch window.
  const { mutate: removePaymentMethod, isPending: isRemoving } =
    useBrokerageFirmControllerDeletePaymentMethod({ mutation: { onSuccess: () => refetch() } });
  const removalInFlight = isRemoving || isRefetching;

  // Brokerage firms have no product config, so the backend restricts nothing by type:
  // any ACTIVE method counts as a way to be billed.
  const canRemove = (paymentMethodId: string): boolean =>
    !readonly && !isLastChargeableMethod(paymentMethods?.data ?? [], paymentMethodId, []);

  const allDocuments = useMemo<DocumentViewerDocument[]>(() => {
    if (!paymentMethods?.data) return [];
    return paymentMethods.data.flatMap(
      pm =>
        pm.documents?.map(doc => ({
          id: doc.id,
          name: doc.name,
          signedUrl: doc.signedUrl,
          createdAt: doc.createdAt,
          type: doc.type,
          category: doc.category,
          metadata: keyValueFieldsToObject(doc.metadata),
        })) ?? [],
    );
  }, [paymentMethods]);

  const { selectedDocument, viewableDocuments, selectDocument, closeViewer } =
    useDocumentViewer(allDocuments);

  const handleViewDocument = (doc: NonNullable<PaymentMethodWithDocumentsDto['documents']>[0]) => {
    selectDocument({
      id: doc.id,
      name: doc.name,
      signedUrl: doc.signedUrl,
      createdAt: doc.createdAt,
      type: doc.type,
      category: doc.category,
      metadata: keyValueFieldsToObject(doc.metadata),
    });
  };

  const { mutateAsync: generateDocumentsForSignature } =
    useBrokerageFirmControllerGenerateDocumentsForSignature();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Méthodes de paiement</CardTitle>
        </CardHeader>
        <CardContent>
          {status === 'pending' ? (
            <Skeleton className="h-[100px]" />
          ) : status === 'error' ? (
            error && 'status' in error && error.status === 404 ? (
              <div className="text-center text-sm text-muted-foreground">
                Aucune méthode de paiement renseignée, continuez d'abord l&apos;édition du devis
                pour en ajouter une.
              </div>
            ) : (
              <div className="text-center text-sm text-muted-foreground">
                Une erreur est survenue lors de la récupération des méthodes de paiement
              </div>
            )
          ) : paymentMethods.totalCount! > 0 ? (
            <Stack gap={6}>
              {sepaPaymentMethods.length > 0 && (
                <Stack gap={6} className="sm:gap-2">
                  <div className="font-semibold">Méthodes de paiement par SEPA</div>
                  {sepaPaymentMethods.map(paymentMethod => (
                    <IntermediaryPaymentMethod
                      key={paymentMethod.id}
                      paymentMethod={paymentMethod}
                      brokerageFirmId={brokerageFirmId}
                      showMandateSignatureButton={showMandateSignatureButton}
                      canBeDefault={canBeDefault?.(paymentMethod) ?? false}
                      canRemove={canRemove(paymentMethod.id)}
                      onRemove={() =>
                        removePaymentMethod({ brokerageFirmId, paymentMethodId: paymentMethod.id })
                      }
                      isRemoving={removalInFlight}
                      onViewDocument={handleViewDocument}
                      refetch={refetch}
                    />
                  ))}
                </Stack>
              )}
              {cardPaymentMethods.length > 0 && (
                <Stack gap={6} className="sm:gap-2">
                  <div className="font-semibold">Méthodes de paiement par carte</div>
                  {cardPaymentMethods.map(paymentMethod => (
                    <IntermediaryPaymentMethod
                      key={paymentMethod.id}
                      paymentMethod={paymentMethod}
                      brokerageFirmId={brokerageFirmId}
                      canBeDefault={canBeDefault?.(paymentMethod) ?? false}
                      canRemove={canRemove(paymentMethod.id)}
                      onRemove={() =>
                        removePaymentMethod({ brokerageFirmId, paymentMethodId: paymentMethod.id })
                      }
                      isRemoving={removalInFlight}
                      onViewDocument={handleViewDocument}
                      refetch={refetch}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          ) : (
            <div className="text-sm text-muted-foreground">
              Pas de méthode de paiement renseignée.
            </div>
          )}
        </CardContent>
        {status === 'success' && !readonly ? (
          <CardFooter>
            <IntermediaryAddPaymentMethodDialog
              brokerageFirmId={brokerageFirmId}
              stripePromise={stripePromise}
              handleSuccess={async () => {
                await retryUntilValue(() => refetch({ throwOnError: true }), {
                  ...PAYMENT_METHOD_WEBHOOK_RETRY,
                  accessor: result => result?.data,
                  isValidResult: value => value?.totalCount! !== paymentMethods.totalCount,
                });
                await generateDocumentsForSignature({
                  brokerageFirmId,
                  params: { documentTypes: ['SEPA_MANDATE'] },
                });
              }}
              trigger={<Button size="sm">Ajouter une méthode de paiement</Button>}
            />
          </CardFooter>
        ) : (
          <CardFooter />
        )}
      </Card>

      <DocumentViewer
        document={selectedDocument}
        documents={viewableDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
      />
    </>
  );
}

function PaymentMethodLabel({ paymentMethod }: { paymentMethod: PaymentMethodDto }) {
  if (paymentMethod.paymentMethodType === 'sepa_debit') {
    return `IBAN terminant par ${paymentMethod?.last4}`;
  }

  const base = `Carte bancaire terminant par ${paymentMethod?.last4}`;
  if (paymentMethod.expirationYear && paymentMethod.expirationMonth) {
    return `${base} (${new Date(
      paymentMethod.expirationYear,
      paymentMethod.expirationMonth - 1,
    ).toLocaleDateString('fr-FR', { month: '2-digit', year: 'numeric' })})`;
  }
  return base;
}

export type IntermediaryPaymentMethodProps = {
  brokerageFirmId: string;
  paymentMethod: PaymentMethodWithDocumentsDto;
  showMandateSignatureButton?: boolean;
  onViewDocument?(doc: NonNullable<PaymentMethodWithDocumentsDto['documents']>[0]): void;
  refetch?(): void;
  canBeDefault?: boolean;
  canRemove?: boolean;
  onRemove?(): void;
  isRemoving?: boolean;
};

export function IntermediaryPaymentMethod({
  brokerageFirmId,
  paymentMethod,
  showMandateSignatureButton,
  onViewDocument,
  refetch,
  canBeDefault,
  canRemove,
  onRemove,
  isRemoving,
}: IntermediaryPaymentMethodProps) {
  const { toast } = useToast();

  const {
    mutate: generateMandateSignatureRequest,
    isPending: isGenerateMandateSignatureRequestLoading,
  } = useBrokerageFirmControllerGenerateMandateSignatureRequest({
    mutation: {
      onSuccess: () => {
        toast({
          title: 'Mail de signature envoyé',
          description:
            'Vous allez recevoir un mail pour signer le mandat sepa de la nouvelle méthode de paiement.',
        });
      },
    },
  });

  const { mutate: updatePreferredPaymentMethod, isPending: isUpdatePreferredLoading } =
    useBrokerageFirmControllerUpdatePreferredPaymentMethod({
      mutation: { onSuccess: () => refetch?.() },
    });

  return (
    <Stack
      align="start"
      justify="start"
      gap={0}
      className="sm:items-center sm:justify-between sm:flex-row"
    >
      <Group gap={0} className="space-x-3">
        <Group gap={0}>
          <div className="p-2 bg-muted text-muted-foreground mr-2 rounded-md">
            {paymentMethod.paymentMethodType === 'sepa_debit' ? (
              <LandmarkIcon className="h-5 w-5" />
            ) : (
              <CreditCardIcon className="h-5 w-5" />
            )}
          </div>
          <PaymentMethodLabel paymentMethod={paymentMethod} />
        </Group>
        {paymentMethod.isPreferred && (
          <Badge variant="light" tone="info" size="sm">
            Méthode par défaut
          </Badge>
        )}
        <PaymentMethodStatusBadge status={paymentMethod.status} />
        {paymentMethod.billingDetails && (
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <div>{paymentMethod.billingDetails.name}</div>
                <div>Ajouté le {formatDate(new Date(paymentMethod.createdAt))}</div>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </Group>
      <Group gap={0}>
        {paymentMethod.documents && paymentMethod.documents.length > 0 && (
          <Group gap={1}>
            <DocumentDownloadButton
              document={{
                id: paymentMethod.documents[0].id,
                metadata: keyValueFieldsToObject(paymentMethod.documents[0].metadata),
                signedUrl: paymentMethod.documents[0].signedUrl,
                name: paymentMethod.documents[0].name,
              }}
              showButtonText={false}
            />
            <Button
              onClick={() => onViewDocument?.(paymentMethod.documents![0])}
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:text-foreground hover:bg-slate-100"
            >
              <EyeIcon className="w-4 h-4" aria-label="show-iban" />
            </Button>
          </Group>
        )}
        {paymentMethod.status === 'WAITING_MANDATE_SIGNATURE' && showMandateSignatureButton && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              generateMandateSignatureRequest({
                brokerageFirmId,
                paymentMethodId: paymentMethod.id,
                data: { deliveryMode: 'EMAIL' },
              })
            }
            disabled={isGenerateMandateSignatureRequestLoading}
          >
            Envoyer l&apos;email de signature
          </Button>
        )}
        {!paymentMethod.isPreferred && paymentMethod.status === 'ACTIVE' && canBeDefault && (
          <Button
            size="sm"
            variant="ghost"
            disabled={isUpdatePreferredLoading}
            onClick={() =>
              updatePreferredPaymentMethod({ brokerageFirmId, paymentMethodId: paymentMethod.id })
            }
          >
            Choisir comme défaut
          </Button>
        )}
        {canRemove && (
          <RemovePaymentMethodDialog onConfirm={() => onRemove?.()} isPending={isRemoving} />
        )}
      </Group>
    </Stack>
  );
}

export type IntermediaryAddPaymentMethodDialogProps = {
  brokerageFirmId: string;
  trigger: ReactNode;
  handleSuccess?(): Promise<void> | void;
  stripePromise: Promise<Stripe | null>;
};

export function IntermediaryAddPaymentMethodDialog({
  brokerageFirmId,
  trigger,
  handleSuccess,
  stripePromise,
}: IntermediaryAddPaymentMethodDialogProps) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <VisuallyHidden>
          <DialogTitle>Ajout d'un moyen de paiement</DialogTitle>
          <Description>Formulaire d'ajout d'un moyen de paiement</Description>
        </VisuallyHidden>
        <IntermediaryPaymentMethodSetup
          brokerageFirmId={brokerageFirmId}
          stripePromise={stripePromise}
          confirmSuccess={async () => {
            setOpen(false);
            await handleSuccess?.();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
